import { GeckoComponentModel } from 'types'

const NAVY_ORGANIZATION_ID = 'us-navy-69a72a'
const DECIMAL_RADIX = 10
const CLASS_CAPTURE_INDEX = 1
const NUMBER_CAPTURE_INDEX = 2
const RANGE_START_INDEX = 0
const RANGE_END_INDEX = 1
const RANGE_SPLIT_LIMIT = 2
const RANGE_DELIMITER = '-'

const UNIT_NAME_PARENS_PATTERN = /\(([^)]+)\)/
const UNIT_CLASS_AND_HULL_PATTERN = /\b([a-z]+)[\s-]*(\d+)\b/i
const TEMPLATE_CLASS_AND_BUCKET_PATTERN = /-([a-z]+)-(\d+(?:-\d+)?)$/i

type UnitHullIdentifier = {
  shipClass: string
  hullNumber: number
}

type TemplateHullBucket = {
  shipClass: string
  hullRange: [number, number]
}

// Extract ship class and hull number from a unit display name.
const parseUnitHullIdentifier = (
  unitName: string
): UnitHullIdentifier | null => {
  const parensMatch = unitName.match(UNIT_NAME_PARENS_PATTERN)
  if (!parensMatch) {
    return null
  }

  const classAndHullMatch = parensMatch[CLASS_CAPTURE_INDEX].match(
    UNIT_CLASS_AND_HULL_PATTERN
  )
  if (!classAndHullMatch) {
    return null
  }

  const hullNumber = Number.parseInt(
    classAndHullMatch[NUMBER_CAPTURE_INDEX],
    DECIMAL_RADIX
  )
  if (Number.isNaN(hullNumber)) {
    return null
  }

  return {
    shipClass: classAndHullMatch[CLASS_CAPTURE_INDEX].toUpperCase(),
    hullNumber,
  }
}

// Parse template id suffix into ship class plus exact/range hull bucket.
const parseTemplateHullBucket = (
  templateId: string
): TemplateHullBucket | null => {
  const bucketMatch = templateId.match(TEMPLATE_CLASS_AND_BUCKET_PATTERN)
  if (!bucketMatch) {
    return null
  }

  const shipClass = bucketMatch[CLASS_CAPTURE_INDEX].toUpperCase()
  const bucket = bucketMatch[NUMBER_CAPTURE_INDEX]

  if (!bucket.includes(RANGE_DELIMITER)) {
    const exact = Number.parseInt(bucket, DECIMAL_RADIX)
    if (Number.isNaN(exact)) {
      return null
    }

    return {
      shipClass,
      hullRange: [exact, exact],
    }
  }

  const [startRaw, endRaw] = bucket.split(RANGE_DELIMITER, RANGE_SPLIT_LIMIT)
  const start = Number.parseInt(startRaw, DECIMAL_RADIX)
  const end = Number.parseInt(endRaw, DECIMAL_RADIX)
  if (Number.isNaN(start) || Number.isNaN(end) || start > end) {
    return null
  }

  return {
    shipClass,
    hullRange: [start, end],
  }
}

// Check whether a unit's class/hull falls within a template's class bucket.
const isUnitHullApplicableToTemplate = (
  unitHullIdentifier: UnitHullIdentifier,
  template: GeckoComponentModel
): boolean => {
  const templateHullBucket = parseTemplateHullBucket(template.id)
  if (!templateHullBucket) {
    return false
  }

  if (unitHullIdentifier.shipClass !== templateHullBucket.shipClass) {
    return false
  }

  return (
    unitHullIdentifier.hullNumber >=
      templateHullBucket.hullRange[RANGE_START_INDEX] &&
    unitHullIdentifier.hullNumber <=
      templateHullBucket.hullRange[RANGE_END_INDEX]
  )
}

// Filter templates for Navy contexts using unit class and hull number.
export const getApplicableTemplatesForUnitContext = ({
  templates,
  organizationTag,
  unitName,
}: {
  templates: GeckoComponentModel[]
  organizationTag: string | undefined
  unitName: string | undefined
}): GeckoComponentModel[] => {
  if (organizationTag !== NAVY_ORGANIZATION_ID || !unitName) {
    return templates
  }

  const unitHullIdentifier = parseUnitHullIdentifier(unitName)
  if (!unitHullIdentifier) {
    return templates
  }

  return templates.filter((template) =>
    isUnitHullApplicableToTemplate(unitHullIdentifier, template)
  )
}
